/**
 * Coloration PDP — custom element for the coloration product pages.
 *
 * Ported from the LV Horizon theme (coloration-pdp.js), rewritten for Grid:
 * no Horizon Component base class, vanilla refs like capillaire-pdp.js.
 *
 * Handles: quiz-origin gating (buy UI only shows after the colour quiz),
 * kit mode (?kit=1: tubes from the quiz + oxydant + promo bundle in one add),
 * one-time vs subscription pricing, oxydant add-on, gallery thumbs and
 * AJAX add-to-cart through window.LVTheme.cart.
 */
function shopifyRoute(path) {
  if (window.LVTheme?.route) return window.LVTheme.route(path);
  const root = window.Shopify?.routes?.root || '/';
  return root + path.replace(/^\/+/, '');
}

/* Count label, e.g. "2 tubes" — texts come from the theme locale via data attributes on the host */
function tubesLabel(el, count) {
  const tpl = count === 1 ? el.dataset.kitTubeOne : el.dataset.kitTubeOther;
  return (tpl || '[count]').replace('[count]', count);
}

function formatMoneyCents(cents) {
  const currency = window.Shopify?.currency?.active || 'CAD';
  let locale = document.documentElement.lang || 'fr';
  // Canadian dollars read "14,99 $" (fr) / "$14.99" (en), not "14,99 $CA"
  if (currency === 'CAD' && !locale.includes('-')) locale = locale.slice(0, 2) + '-CA';
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
  } catch (_) {
    return (cents / 100).toFixed(2) + ' $';
  }
}

class ColorationPdp extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.refs = {};
    this.querySelectorAll('[ref]').forEach((el) => {
      const raw = el.getAttribute('ref');
      const key = raw.replace('[]', '');
      if (raw.endsWith('[]')) {
        (this.refs[key] = this.refs[key] || []).push(el);
      } else {
        this.refs[key] = el;
      }
    });

    this._applyQuizOriginFlag();

    this.sellingPlanId = this.dataset.sellingPlanId;
    this.sellingPlanDiscount = parseInt(this.dataset.sellingPlanDiscount, 10) || 0;

    const oxydantJson = this.querySelector('[data-oxydant-variants]');
    try {
      this.oxydantVariants = oxydantJson ? JSON.parse(oxydantJson.textContent) : [];
    } catch (_) {
      this.oxydantVariants = [];
    }

    this._bindEvents();
    this._initKitMode();
    this._initStickyBar();
  }

  _bindEvents() {
    const r = this.refs;
    (r.thumbnailButtons || []).forEach((btn) => {
      btn.addEventListener('click', () => this.selectMedia(parseInt(btn.dataset.index, 10)));
    });
    (r.variantBtns || []).forEach((btn) => {
      btn.addEventListener('click', () => this._handleVariantClick(btn));
    });
    r.onetimeRadio?.addEventListener('change', () => this.handlePurchaseTypeChange());
    r.subscriptionRadio?.addEventListener('change', () => this.handlePurchaseTypeChange());
    r.oxydantCheckbox?.addEventListener('change', () => this.handleOxydantToggle());
    r.oxydantDismissCheckbox?.addEventListener('change', () => this.handleOxydantDismiss());
    (r.oxydantSizeRadios || []).forEach((el) => el.addEventListener('change', () => this._onOxydantChange()));
    (r.oxydantVolumeRadios || []).forEach((el) => el.addEventListener('change', () => this._onOxydantChange()));
    r.addToCartButton?.addEventListener('click', (e) => this.handleAddToCart(e));
    r.kitEditToggle?.addEventListener('click', () => this._toggleKitEditor());
    r.kitEditorDone?.addEventListener('click', () => this._toggleKitEditor(false));
    r.kitLevelSelect?.addEventListener('change', () => this._onKitLevelChange());
    r.kitWhiteSelect?.addEventListener('change', () => this._onKitWhiteChange());
  }

  /* ── Sticky add-to-cart bar (phones) ── */

  _initStickyBar() {
    const bar = this.refs.stickyBar;
    const mainBtn = this.refs.addToCartButton;
    if (!bar || !mainBtn) return;
    this.refs.stickyButton?.addEventListener('click', () => mainBtn.click());
    const update = () => {
      const rect = mainBtn.getBoundingClientRect();
      bar.hidden = !(rect.bottom < 0);
      const btn = this.refs.stickyButton;
      if (btn) {
        btn.disabled = mainBtn.disabled;
        btn.textContent = mainBtn.textContent;
      }
    };
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ── Quiz origin: unlock buy/subscribe UI when the visitor came from the colour quiz ── */

  _applyQuizOriginFlag() {
    let fromQuiz = false;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('kit') === '1') fromQuiz = true;
      if (!fromQuiz && sessionStorage.getItem('lv_from_color_quiz') === '1') fromQuiz = true;
    } catch (_) { /* ignore */ }

    if (fromQuiz) {
      try { sessionStorage.setItem('lv_from_color_quiz', '1'); } catch (_) { /* ignore */ }
      this.dataset.fromQuiz = 'true';
      document.documentElement.dataset.fromColorQuiz = 'true';
    }
  }

  /* ── Kit mode (from quiz redirect) ── */

  _initKitMode() {
    const params = new URLSearchParams(window.location.search);
    const kitParam = params.get('kit');
    if (kitParam !== '1') return; // a kit only exists after the quiz: no free formula building

    let kitData = null;
    try {
      kitData = JSON.parse(localStorage.getItem('cq_kit'));
      if (kitData?._ts && Date.now() - kitData._ts > 3600000) {
        localStorage.removeItem('cq_kit');
        kitData = null;
      }
    } catch (_) { /* ignore */ }

    if (!kitData || !kitData.items?.length) return;

    this.kitMode = true;
    this.kitData = kitData;
    // A kit always comes from the quiz (?kit=1), so it always gets the free promo bundle
    this.kitFromQuiz = true;
    // Identifies one quiz result, so a kit whose formula was changed can replace the earlier one in the cart
    this.kitOrigin = String(kitData.origin || kitData._ts || Date.now());
    // Established formulas of this shade (one per natural level x white hair), written by the quiz
    this.kitOptions = Array.isArray(kitData.options) ? kitData.options : [];
    this.kitCatalog = kitData.catalog || {};
    this.kitLevel = kitData.level != null ? String(kitData.level) : '';
    this.kitWhite = kitData.white != null ? String(kitData.white) : '';
    this.kitName = kitData.name || '';
    this.kitFormula = kitData.formula || '';
    this.kitVolume = kitData.vol || '20';
    this.kitItems = kitData.items; // [{variantId, handle, name, code, price, img}, ...]
    const r = this.refs;

    if (r.kitBadge) r.kitBadge.hidden = false;
    // "Modifier ma formule" only exists while a formula is active and has other established formulas to pick from
    if (r.kitEditToggle) r.kitEditToggle.hidden = this.kitOptions.length < 2;
    if (r.productDescription) r.productDescription.hidden = true;
    if (r.priceContainer) r.priceContainer.hidden = true;
    if (r.kitPriceContainer) r.kitPriceContainer.hidden = false;
    if (r.variantPicker) r.variantPicker.hidden = true;
    this._renderKit();

    // Oxydant: pre-select the volume from the formula
    if (r.oxydantVolumeRadios) {
      r.oxydantVolumeRadios.forEach((radio) => {
        const match = radio.value.match(/(\d+)/);
        radio.checked = !!(match && match[1] === this.kitVolume);
      });
      this._updateOxydantPrice();
    }
    if (r.oxydantQuizNote && this.kitVolume) {
      const template = r.oxydantQuizNote.dataset.quizNoteTemplate || '';
      r.oxydantQuizNote.textContent = template.replace('[vol]', this.kitVolume);
      r.oxydantQuizNote.hidden = false;
    }
    if (r.oxydantCheckbox && !r.oxydantCheckbox.checked) {
      r.oxydantCheckbox.checked = true;
      this.handleOxydantToggle();
    }
    this._updateKitPrice();
    this._renderKitEditor();
  }

  /* Title, subtitle, bundle visual and totals — redrawn whenever the kit changes */
  _renderKit() {
    const r = this.refs;

    if (r.productTitle) {
      const kitTitle = this.kitItems.map((i) => i.name).join(' + ');
      r.productTitle.childNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) node.textContent = '';
      });
      r.productTitle.insertAdjacentText('afterbegin', kitTitle + ' ');
    }

    if (r.kitSubtitle) {
      const parts = [];
      if (this.kitName) parts.push((this.dataset.kitFor || '[name]').replace('[name]', this.kitName));
      if (this.kitFormula) parts.push(this.kitFormula);
      r.kitSubtitle.textContent = parts.join(' · ');
      r.kitSubtitle.hidden = !parts.length;
    }

    if (r.kitMedia && r.kitMediaStack) {
      if (r.mediaGallery) r.mediaGallery.hidden = true;
      r.kitMedia.hidden = false;
      r.kitMediaStack.textContent = '';
      this.kitItems.forEach((item) => {
        if (!item.img) return;
        const img = document.createElement('img');
        img.src = item.img;
        img.alt = item.name;
        img.width = 180;
        img.height = 180;
        img.loading = 'eager';
        r.kitMediaStack.appendChild(img);
      });
      if (r.kitMediaLabel) {
        const count = this.kitItems.length;
        r.kitMediaLabel.textContent = `${tubesLabel(this, count)} + ${this.dataset.kitOxydant || ''}`.trim();
      }
    }

    // Price: total of every kit item (item.price is in dollars)
    this.kitTotalCents = Math.round(this.kitItems.reduce((sum, i) => sum + (i.price || 0) * 100, 0));
    this._refreshKitPurchaseCards();
    this._updateKitPrice();
  }

  /* ── Formula picker: switch between the ESTABLISHED formulas of this shade (natural level x white hair) ── */

  _toggleKitEditor(open) {
    const r = this.refs;
    if (!r.kitEditor) return;
    const show = typeof open === 'boolean' ? open : r.kitEditor.hidden;
    r.kitEditor.hidden = !show;
    r.kitEditToggle?.setAttribute('aria-expanded', String(show));
  }

  _kitOptionFor(level, white) {
    return this.kitOptions.find((o) => String(o.n) === String(level) && String(o.w) === String(white)) || null;
  }

  _renderKitEditor() {
    const r = this.refs;
    if (!r.kitLevelSelect || !r.kitWhiteSelect || this.kitOptions.length < 2) return;
    const levels = [...new Set(this.kitOptions.map((o) => String(o.n)))].sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
    const levelLabels = this.kitData?.levelLabels || {};
    const whiteLabels = this.kitData?.whiteLabels || {};
    const fill = (select, values, labels, current) => {
      select.textContent = '';
      values.forEach((v) => {
        const opt = document.createElement('option');
        opt.value = v;
        opt.textContent = labels[v] || v;
        if (v === current) opt.selected = true;
        select.appendChild(opt);
      });
    };
    fill(r.kitLevelSelect, levels, levelLabels, this.kitLevel);
    const whites = [...new Set(this.kitOptions.filter((o) => String(o.n) === this.kitLevel).map((o) => String(o.w)))];
    fill(r.kitWhiteSelect, whites, whiteLabels, this.kitWhite);
    if (r.kitEditorFormula) r.kitEditorFormula.textContent = this.kitFormula || '';
  }

  _onKitLevelChange() {
    const level = this.refs.kitLevelSelect.value;
    const whites = this.kitOptions.filter((o) => String(o.n) === level).map((o) => String(o.w));
    const white = whites.includes(this.kitWhite) ? this.kitWhite : whites[0];
    this._applyKitOption(level, white);
  }

  _onKitWhiteChange() {
    this._applyKitOption(this.kitLevel, this.refs.kitWhiteSelect.value);
  }

  _applyKitOption(level, white) {
    const option = this._kitOptionFor(level, white);
    if (!option) return;
    const items = option.codes.map((code) => this.kitCatalog[code]).filter(Boolean);
    if (!items.length || items.length !== option.codes.length) return; // a tube is missing from the saved catalog: keep the current kit
    // Several natural levels share one established formula: tell the customer when a choice changes nothing.
    const sameFormula = option.formula === this.kitFormula && String(option.vol || '20') === String(this.kitVolume);
    this.kitLevel = String(level);
    this.kitWhite = String(white);
    this.kitItems = items;
    this.kitFormula = option.formula;
    this.kitVolume = String(option.vol || '20');
    const r = this.refs;
    if (r.oxydantVolumeRadios) {
      r.oxydantVolumeRadios.forEach((radio) => {
        const match = radio.value.match(/(\d+)/);
        radio.checked = !!(match && match[1] === this.kitVolume);
      });
      this._updateOxydantPrice();
    }
    if (r.oxydantQuizNote) {
      const template = r.oxydantQuizNote.dataset.quizNoteTemplate || '';
      r.oxydantQuizNote.textContent = template.replace('[vol]', this.kitVolume);
    }
    this._kitChanged();
    this._renderKitEditor();
    const note = r.kitEditorNote;
    if (note) note.textContent = (sameFormula ? note.dataset.noteSame : note.dataset.noteChanged) || '';
  }

  /* Redraw the page and keep the kit (same quiz result, new formula) for a page refresh */
  _kitChanged() {
    this._renderKit();
    try {
      localStorage.setItem('cq_kit', JSON.stringify({
        ...(this.kitData || {}),
        name: this.kitName,
        formula: this.kitFormula,
        vol: this.kitVolume,
        items: this.kitItems,
        level: this.kitLevel,
        white: this.kitWhite,
        origin: this.kitOrigin,
        _ts: Date.now(),
      }));
    } catch (_) { /* ignore */ }
  }

  _refreshKitPurchaseCards() {
    const r = this.refs;
    const total = this.kitTotalCents;
    const discounted = Math.round(total * (1 - this.sellingPlanDiscount / 100));
    if (r.onetimePrice) r.onetimePrice.textContent = formatMoneyCents(total);
    if (r.subscriptionPrice) r.subscriptionPrice.textContent = formatMoneyCents(discounted);
    if (r.subscriptionSavings) {
      const label = this.dataset.savingsLabel || '';
      r.subscriptionSavings.textContent = `${label} ${formatMoneyCents(total - discounted)}`;
    }
  }

  _updateKitPrice() {
    if (!this.kitMode) return;
    const r = this.refs;
    const isSubscription = r.subscriptionRadio.checked;

    let total = this.kitTotalCents;
    const oxydantVariant = r.oxydantCheckbox?.checked ? this._getSelectedOxydantVariant() : null;
    if (oxydantVariant) total += oxydantVariant.price;

    const displayPrice = isSubscription
      ? Math.round(total * (1 - this.sellingPlanDiscount / 100))
      : total;

    if (r.kitPriceTotal) r.kitPriceTotal.textContent = formatMoneyCents(displayPrice);
    if (r.kitPriceDetail) {
      const parts = [tubesLabel(this, this.kitItems.length)];
      if (oxydantVariant) parts.push(this.dataset.kitOxydant || '');
      r.kitPriceDetail.textContent = parts.join(' + ') + (isSubscription ? ` · ${this.dataset.kitSubscription || ''}` : '');
    }
  }

  /* ── Gallery ── */

  selectMedia(index) {
    (this.refs.mediaSlides || []).forEach((slide, i) => {
      slide.hidden = i !== index;
    });
    (this.refs.thumbnailButtons || []).forEach((thumb, i) => {
      thumb.classList.toggle('coloration-pdp__thumb--active', i === index);
      thumb.setAttribute('aria-pressed', String(i === index));
    });
  }

  /* ── Variant picker (products with more than one variant) ── */

  _handleVariantClick(btn) {
    if (this.kitMode) return;
    const r = this.refs;
    (r.variantBtns || []).forEach((b) => {
      const active = b === btn;
      b.classList.toggle('coloration-pdp__variant-btn--active', active);
      b.setAttribute('aria-checked', String(active));
    });
    r.variantIdInput.value = btn.dataset.variantId;
    const price = parseInt(btn.dataset.variantPrice, 10);
    const priceEl = r.priceContainer?.querySelector('.coloration-pdp__price-current');
    if (priceEl) priceEl.textContent = formatMoneyCents(price);
    r.priceContainer?.querySelector('.coloration-pdp__price-compare')?.remove();
    this._updateSubscriptionPrice(price);
    const available = btn.dataset.variantAvailable === 'true';
    if (available) {
      r.addToCartButton.removeAttribute('disabled');
      r.addToCartButton.textContent = this.dataset.addToCartText;
    } else {
      r.addToCartButton.setAttribute('disabled', '');
      r.addToCartButton.textContent = this.dataset.soldOutText;
    }
  }

  _updateSubscriptionPrice(price) {
    const r = this.refs;
    const discounted = Math.round(price * (1 - this.sellingPlanDiscount / 100));
    r.subscriptionPrice.textContent = formatMoneyCents(discounted);
    r.onetimePrice.textContent = formatMoneyCents(price);
    if (r.subscriptionSavings) {
      const label = this.dataset.savingsLabel || '';
      r.subscriptionSavings.textContent = `${label} ${formatMoneyCents(price - discounted)}`;
    }
  }

  /* ── Purchase type toggle ── */

  handlePurchaseTypeChange() {
    const r = this.refs;
    const isSubscription = r.subscriptionRadio.checked;

    r.onetimeLabel.classList.toggle('coloration-pdp__purchase-card--active', !isSubscription);
    r.subscriptionLabel.classList.toggle('coloration-pdp__purchase-card--active', isSubscription);
    r.subInfo.hidden = !isSubscription;
    r.sellingPlanInput.value = isSubscription ? this.sellingPlanId : '';

    if (this.kitMode) {
      this._refreshKitPurchaseCards();
      this._updateKitPrice();
    }
    this._updateOxydantPrice();
  }

  /* ── Oxydant add-on ── */

  handleOxydantToggle() {
    const r = this.refs;
    if (!r.oxydantCheckbox) return;
    const include = r.oxydantCheckbox.checked;
    if (r.oxydantDismissCheckbox) r.oxydantDismissCheckbox.checked = !include;
    if (r.oxydantBody) r.oxydantBody.hidden = !include;
    if (this.kitMode) this._updateKitPrice();
  }

  handleOxydantDismiss() {
    const r = this.refs;
    if (!r.oxydantDismissCheckbox) return;
    const dismissed = r.oxydantDismissCheckbox.checked;
    if (r.oxydantCheckbox) r.oxydantCheckbox.checked = !dismissed;
    if (r.oxydantBody) r.oxydantBody.hidden = dismissed;
    if (this.kitMode) this._updateKitPrice();
  }

  _onOxydantChange() {
    this._updateOxydantPrice();
    if (this.kitMode) {
      const vol = this._getSelectedOxydantVariant()?.option1;
      if (vol) {
        this.kitVolume = String(parseInt(vol, 10));
        this.kitFormula = /\d+\s*vol\s*$/i.test(this.kitFormula || '')
          ? this.kitFormula.replace(/\d+\s*vol\s*$/i, `${this.kitVolume}vol`)
          : `${this.kitFormula || ''}${this.kitFormula ? ' + ' : ''}${this.kitVolume}vol`;
      }
      this._kitChanged();
      if (this.refs.kitEditorFormula) this.refs.kitEditorFormula.textContent = this.kitFormula || '';
    }
  }

  _getSelectedOxydantVariant() {
    const r = this.refs;
    if (!r.oxydantSizeRadios || !r.oxydantVolumeRadios) return null;
    const sizeRadio = r.oxydantSizeRadios.find((el) => el.checked);
    const volumeRadio = r.oxydantVolumeRadios.find((el) => el.checked);
    if (!sizeRadio || !volumeRadio) return null;
    return this.oxydantVariants.find(
      (v) => v.option1 === volumeRadio.value && v.option2 === sizeRadio.value
    ) || null;
  }

  _updateOxydantPrice() {
    const variant = this._getSelectedOxydantVariant();
    if (variant && this.refs.oxydantPrice) {
      const isSubscription = this.refs.subscriptionRadio.checked;
      const price = (isSubscription && variant.sellingPlanId)
        ? Math.round(variant.price * (1 - this.sellingPlanDiscount / 100))
        : variant.price;
      this.refs.oxydantPrice.textContent = formatMoneyCents(price);
    }
  }

  /* ── Add to cart ── */

  /* Fingerprint of the kit's content (tubes + oxydant): a re-add with the same content is a second kit, a changed one replaces the first */
  _kitSignature() {
    const oxy = this.refs.oxydantCheckbox?.checked ? this._getSelectedOxydantVariant() : null;
    return this.kitItems.map((i) => i.variantId).sort().join('-') + (oxy ? `|${oxy.id}` : '');
  }

  _buildItems(skipPromo) {
    const r = this.refs;
    const isSubscription = r.subscriptionRadio.checked;
    const sellingPlan = isSubscription ? parseInt(this.sellingPlanId, 10) : null;
    const includeOxydant = r.oxydantCheckbox?.checked;
    const oxydantVariant = includeOxydant ? this._getSelectedOxydantVariant() : null;
    const bundleTitle = isSubscription
      ? (this.dataset.bundleTitleSubscription || 'Coloration Pro - Kit (Abonnement)')
      : (this.dataset.bundleTitleOnetime || 'Kit Coloration Pro');
    const items = [];

    const bundleProps = (key, parent) => ({
      _bundle_key: key,
      _bundle_title: bundleTitle,
      ...(parent ? { _bundle_parent: 'true' } : {}),
    });
    const addOxydant = (key) => {
      if (!oxydantVariant) return;
      const item = { id: oxydantVariant.id, quantity: 1, properties: key ? bundleProps(key, false) : {} };
      if (sellingPlan && oxydantVariant.sellingPlanId) item.selling_plan = oxydantVariant.sellingPlanId;
      if (this.kitMode) {
        item.properties._kit_origin = this.kitOrigin;
        item.properties._kit_sig = this._kitSignature();
        if (this.kitName) item.properties['Formule pour'] = this.kitName;
        if (this.kitFormula) item.properties.Formule = this.kitFormula;
      }
      items.push(item);
    };

    if (this.kitMode) {
      const key = `coloration-kit-${Date.now()}`;
      this.kitItems.forEach((kit, idx) => {
        const item = {
          id: parseInt(kit.variantId, 10),
          quantity: 1,
          properties: { ...bundleProps(key, idx === 0), _kit_origin: this.kitOrigin, _kit_sig: this._kitSignature() },
        };
        if (sellingPlan) item.selling_plan = sellingPlan;
        if (this.kitName) item.properties['Formule pour'] = this.kitName;
        if (this.kitFormula) item.properties.Formule = this.kitFormula;
        items.push(item);
      });
      addOxydant(key);
      // Promo bundle (free gift from the quiz) — zeroed by the quiz-bundle-discount Function
      const promoVariant = this.dataset.promoBundleVariant;
      if (promoVariant && !skipPromo && this.kitFromQuiz) {
        items.push({
          id: parseInt(promoVariant, 10),
          quantity: 1,
          properties: { ...bundleProps(key, false), _quiz_coloration: 'true', _kit_origin: this.kitOrigin, _kit_sig: this._kitSignature() },
        });
      }
    } else {
      // A tube added on its own (with or without oxydant) is a normal product: its own cart lines, each with its own quantity.
      // Only a formula (kit) is grouped into one row. (Before 2026-10-19 tube + oxydant were grouped as "Kit Coloration Pro".)
      const key = null;
      const main = {
        id: parseInt(r.variantIdInput.value, 10),
        quantity: 1,
        properties: {},
      };
      if (sellingPlan) main.selling_plan = sellingPlan;
      items.push(main);
      addOxydant(key);
    }
    return items;
  }

  async handleAddToCart(event) {
    event?.preventDefault();
    const r = this.refs;
    const btn = r.addToCartButton;
    if (!btn || btn.disabled) return;

    // The quiz-bundle-discount Function zeroes ONE promo bundle per cart. A second kit must not add a
    // second promo line, or it would be charged at full price: skip it when the cart already has one.
    let promoAlreadyInCart = false;
    let replaceUpdates = null;
    try {
      const cart = await (await fetch(shopifyRoute('cart.js'))).json();
      const lines = cart.items || [];
      // Same quiz result already in the cart but with a different formula (the customer edited it): the new kit replaces it, promo bundle included.
      // Only lines carrying this kit's own _kit_origin are touched: other kits and other products stay as they are.
      const replaced = new Set();
      const signature = this.kitMode ? this._kitSignature() : '';
      if (this.kitMode && this.kitOrigin) {
        lines.forEach((line) => {
          const props = line.properties || {};
          if (props._kit_origin === this.kitOrigin && props._kit_sig !== signature) replaced.add(line.key);
        });
        if (replaced.size) {
          replaceUpdates = {};
          replaced.forEach((key) => { replaceUpdates[key] = 0; });
        }
      }
      promoAlreadyInCart = lines.some((line) => !replaced.has(line.key) && line.properties && line.properties._quiz_coloration === 'true');
    } catch (_) { /* cart read failed: add as usual */ }
    const items = this._buildItems(promoAlreadyInCart);
    const originalText = btn.textContent;
    btn.classList.add('is-loading');
    btn.textContent = this.dataset.addingText || originalText;
    window.LVTheme?.announce?.(this.dataset.addingText || originalText);
    if (r.addToCartError) r.addToCartError.hidden = true;

    try {
      if (replaceUpdates) {
        const removal = await fetch(shopifyRoute('cart/update.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: replaceUpdates }),
        });
        if (!removal.ok) throw new Error('replace');
      }
      if (window.LVTheme?.cart) {
        await window.LVTheme.cart.addItems(items, { refreshDrawer: true });
      } else {
        const res = await fetch(shopifyRoute('cart/add.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!res.ok) {
          let message = '';
          try { const data = await res.json(); message = data.description || data.message || ''; } catch (_) { /* ignore */ }
          throw new Error(message);
        }
        if (window.cartDrawer?.refresh) await window.cartDrawer.refresh();
      }

      btn.classList.remove('is-loading');
      btn.classList.add('coloration-pdp__btn--success');
      btn.textContent = this.dataset.addedText || originalText;
      window.LVTheme?.announce?.(this.dataset.addedText || originalText);
      // The quiz kit stays saved (1 h) so the customer can come back, change her formula and replace the kit in her cart
      window.cartDrawer?.open?.();

      setTimeout(() => {
        btn.classList.remove('coloration-pdp__btn--success');
        btn.textContent = originalText;
      }, 1500);
    } catch (err) {
      btn.classList.remove('is-loading');
      btn.textContent = originalText;
      if (r.addToCartError) {
        r.addToCartError.textContent = err.message || this.dataset.cartErrorText || '';
        r.addToCartError.hidden = false;
        setTimeout(() => { r.addToCartError.hidden = true; }, 4000);
      }
    }
  }
}

if (!customElements.get('coloration-pdp')) customElements.define('coloration-pdp', ColorationPdp);
